// /app/frontend/src/pages/admin/AdminValuePayoutsPage.jsx
// Admin Value Payout Dashboard - Review and process Appreciation Value payout requests
// PATCH v2: Mobile card mode with no horizontal overflow
// PATCH v3: Added batch approve/reject functionality (P3-B)

import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { 
  DollarSign, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Flag, 
  Search, 
  ChevronRight,
  AlertCircle,
  User,
  CreditCard,
  History,
  RefreshCw,
  Check,
  X,
  Download,
  CheckSquare,
  Square,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from "../../api/client";
import { AuthContext } from '../../App';
import AdminHelp from '../../components/admin/AdminHelp';

const API = window.location.origin;

// Status badge component
const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    approved: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    flagged: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  };

  const icons = {
    pending: <Clock className="w-3 h-3" />,
    approved: <CheckCircle className="w-3 h-3" />,
    rejected: <XCircle className="w-3 h-3" />,
    paid: <Check className="w-3 h-3" />,
    flagged: <Flag className="w-3 h-3" />,
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
      {icons[status]}
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};

// Eligibility chips - compact version
const EligibilityChips = ({ snapshot, compact = false }) => {
  if (!snapshot) return null;

  const chips = [
    { key: 'subscription_active', label: 'Sub', ok: snapshot.subscription_active },
    { key: 'identity_verified', label: 'ID', ok: snapshot.identity_verified },
    { key: 'hold_days_met', label: 'Hold', ok: snapshot.hold_days_met },
    { key: 'min_payout_met', label: 'Min', ok: snapshot.min_payout_met },
    { key: 'cooldown_ok', label: 'Cool', ok: snapshot.cooldown_ok },
  ];

  if (compact) {
    // Only show failed chips on mobile to save space
    const failed = chips.filter(c => !c.ok);
    if (failed.length === 0) {
      return <span className="text-xs text-green-600">✓ All OK</span>;
    }
    return (
      <div className="flex gap-1 flex-wrap">
        {failed.map(chip => (
          <span
            key={chip.key}
            className="text-xs px-1 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
          >
            ✗ {chip.label}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="flex gap-1 flex-wrap">
      {chips.map(chip => (
        <span
          key={chip.key}
          className={`text-xs px-1.5 py-0.5 rounded ${
            chip.ok
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
          }`}
          title={`${chip.label}: ${chip.ok ? 'OK' : 'Failed'}`}
        >
          {chip.ok ? '✓' : '✗'} {chip.label}
        </span>
      ))}
    </div>
  );
};

// Mobile payout card component
const PayoutMobileCard = ({ item, isSelected, onClick, isChecked, onCheckboxChange }) => (
  <div
    className={`p-3 border-b border-gray-100 dark:border-slate-800 last:border-b-0 ${
      isSelected ? 'bg-rose-50 dark:bg-rose-900/10' : isChecked ? 'bg-blue-50 dark:bg-blue-900/10' : 'hover:bg-gray-50 dark:hover:bg-slate-800/50'
    }`}
    data-testid={`payout-mobile-card-${item.id}`}
  >
    {/* Row 1: Checkbox + Email + Status */}
    <div className="flex items-start justify-between gap-2 mb-1.5">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <button
          onClick={(e) => onCheckboxChange(item.id, e)}
          className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-slate-700"
          data-testid={`checkbox-${item.id}`}
        >
          {isChecked ? (
            <CheckSquare className="w-5 h-5 text-rose-500" />
          ) : (
            <Square className="w-5 h-5 text-gray-400" />
          )}
        </button>
        <span 
          className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex-1 cursor-pointer"
          onClick={onClick}
        >
          {item.user_email}
        </span>
      </div>
      <StatusBadge status={item.status} />
    </div>
    
    {/* Row 2: Amount + Date */}
    <div className="flex items-center justify-between mb-1.5 pl-7" onClick={onClick}>
      <span className="text-lg font-bold text-gray-900 dark:text-gray-100 cursor-pointer">
        ${item.amount_usd.toFixed(2)}
      </span>
      <span className="text-xs text-gray-400">
        {new Date(item.created_at).toLocaleDateString()}
      </span>
    </div>
    
    {/* Row 3: Method + Eligibility */}
    <div className="flex items-center justify-between pl-7" onClick={onClick}>
      <span className="text-xs text-gray-500 truncate max-w-[120px] cursor-pointer">
        {item.method}
      </span>
      <EligibilityChips snapshot={item.eligibility_snapshot} compact />
    </div>
  </div>
);

// Desktop table row component
const PayoutTableRow = ({ item, isSelected, onClick, isChecked, onCheckboxChange }) => (
  <tr
    className={`transition ${
      isSelected ? 'bg-rose-50 dark:bg-rose-900/10' : isChecked ? 'bg-blue-50 dark:bg-blue-900/10' : 'hover:bg-gray-50 dark:hover:bg-slate-800/50'
    }`}
    data-testid={`payout-row-${item.id}`}
  >
    <td className="px-3 py-2 w-8">
      <button
        onClick={(e) => onCheckboxChange(item.id, e)}
        className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-slate-700"
        data-testid={`checkbox-${item.id}`}
      >
        {isChecked ? (
          <CheckSquare className="w-5 h-5 text-rose-500" />
        ) : (
          <Square className="w-5 h-5 text-gray-400" />
        )}
      </button>
    </td>
    <td className="px-3 py-2 cursor-pointer" onClick={onClick}>
      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[180px]">
          {item.user_email}
        </span>
      </div>
    </td>
    <td className="px-3 py-2 cursor-pointer" onClick={onClick}>
      <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
        ${item.amount_usd.toFixed(2)}
      </span>
    </td>
    <td className="px-3 py-2 cursor-pointer" onClick={onClick}>
      <span className="text-xs text-gray-500 truncate block max-w-[120px]">
        {item.method}
      </span>
    </td>
    <td className="px-3 py-2 cursor-pointer" onClick={onClick}>
      <StatusBadge status={item.status} />
    </td>
    <td className="px-3 py-2 cursor-pointer" onClick={onClick}>
      <EligibilityChips snapshot={item.eligibility_snapshot} />
    </td>
    <td className="px-3 py-2 cursor-pointer" onClick={onClick}>
      <div className="flex items-center gap-1 text-xs text-gray-400">
        {new Date(item.created_at).toLocaleDateString()}
        <ChevronRight className="w-3 h-3" />
      </div>
    </td>
  </tr>
);

export default function AdminValuePayoutsPage() {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [summary, setSummary] = useState(null);
  const [queue, setQueue] = useState({ items: [], total: 0 });
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Action modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPaidModal, setShowPaidModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [paidReference, setPaidReference] = useState('');
  
  // Batch selection state (P3-B)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBatchApproveModal, setShowBatchApproveModal] = useState(false);
  const [showBatchRejectModal, setShowBatchRejectModal] = useState(false);
  const [batchRejectReason, setBatchRejectReason] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResults, setBatchResults] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch summary KPIs
  const fetchSummary = useCallback(async () => {
    try {
      const res = await apiClient.get(`/admin/value-payouts/summary`, { headers });
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [token]);

  // Fetch queue
  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter, limit: 50 };
      if (searchQuery) params.q = searchQuery;

      const res = await apiClient.get(`/admin/value-payouts/queue`, { headers, params });
      setQueue(res.data);
    } catch (err) {
      console.error('Failed to fetch queue:', err);
      toast.error('Failed to load payout queue');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, searchQuery]);

  // Fetch payout detail
  const fetchDetail = useCallback(async (payoutId) => {
    setDetailLoading(true);
    try {
      const res = await apiClient.get(`/admin/value-payouts/item/${payoutId}`, { headers });
      setSelectedPayout(res.data);
    } catch (err) {
      console.error('Failed to fetch detail:', err);
      toast.error('Failed to load payout details');
    } finally {
      setDetailLoading(false);
    }
  }, [token]);

  // Perform action
  const performAction = async (action, extras = {}) => {
    if (!selectedPayout) return;

    setActionLoading(true);
    try {
      const body = {
        payout_id: selectedPayout.id,
        action,
        ...extras,
      };

      const res = await apiClient.post(`/admin/value-payouts/action`, body, { headers });
      
      toast.success(res.data.message || `Action ${action} completed`);
      
      // Refresh data
      await fetchQueue();
      await fetchSummary();
      await fetchDetail(selectedPayout.id);
      
      // Close modals
      setShowRejectModal(false);
      setShowPaidModal(false);
      setRejectReason('');
      setPaidReference('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object') {
        toast.error(detail.detail || 'Action failed');
        if (detail.reasons) {
          detail.reasons.forEach(r => toast.error(r, { duration: 5000 }));
        }
      } else {
        toast.error(detail || 'Action failed');
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Export CSV
  const exportCSV = async () => {
    try {
      const res = await apiClient.get('/admin/value-payouts/export', {
        params: {
          status: statusFilter || 'all',
          q: (searchQuery || '').trim() || undefined,
        },
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });

      const blob = new Blob([res.data], { type: 'text/csv' });

      // Try to honor backend filename
      const cd = res.headers?.['content-disposition'] || res.headers?.get?.('content-disposition') || '';
      let filename = 'value-payouts.csv';
      const m = String(cd).match(/filename=\"?([^\";]+)\"?/i);
      if (m && m[1]) filename = m[1];

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'CSV export failed');
    }
  };

  // Batch selection helpers (P3-B)
  const toggleSelectAll = () => {
    if (selectedIds.size === queue.items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(queue.items.map(i => i.id)));
    }
  };

  const toggleSelectItem = (id, e) => {
    e?.stopPropagation();
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  // Get selected items data
  const selectedItems = useMemo(() => {
    return queue.items.filter(i => selectedIds.has(i.id));
  }, [queue.items, selectedIds]);

  // Calculate total amount of selected items
  const selectedTotalAmount = useMemo(() => {
    return selectedItems.reduce((sum, i) => sum + (i.amount_usd || 0), 0);
  }, [selectedItems]);

  // Batch approve action
  const batchApprove = async () => {
    if (selectedIds.size === 0) return;
    
    setBatchLoading(true);
    setBatchResults(null);
    
    try {
      const res = await apiClient.post(`/admin/payouts/unified/batch-approve`,
        { payout_ids: Array.from(selectedIds) },
        { headers }
      );
      
      setBatchResults(res.data);
      
      if (res.data.failed === 0) {
        toast.success(`Approved ${res.data.approved} payouts ($${(res.data.total_paid_cents / 100).toFixed(2)})`);
        setSelectedIds(new Set());
        setShowBatchApproveModal(false);
      } else {
        toast.warning(`Approved ${res.data.approved}, ${res.data.failed} failed`);
        // Keep failed items selected for retry
        const failedIds = new Set(
          res.data.results.filter(r => !r.ok).map(r => r.payout_id)
        );
        setSelectedIds(failedIds);
      }
      
      // Refresh data
      await fetchQueue();
      await fetchSummary();
      
    } catch (err) {
      const msg = err.response?.data?.detail || 'Batch approve failed';
      toast.error(typeof msg === 'object' ? msg.message || JSON.stringify(msg) : msg);
    } finally {
      setBatchLoading(false);
    }
  };

  // Batch reject action
  const batchReject = async () => {
    if (selectedIds.size === 0 || batchRejectReason.trim().length < 5) return;
    
    setBatchLoading(true);
    setBatchResults(null);
    
    try {
      const res = await apiClient.post(`/admin/payouts/unified/batch-reject`,
        { 
          payout_ids: Array.from(selectedIds),
          admin_note: batchRejectReason.trim()
        },
        { headers }
      );
      
      setBatchResults(res.data);
      
      if (res.data.failed === 0) {
        toast.success(`Rejected ${res.data.rejected} payouts`);
        setSelectedIds(new Set());
        setShowBatchRejectModal(false);
        setBatchRejectReason('');
      } else {
        toast.warning(`Rejected ${res.data.rejected}, ${res.data.failed} failed`);
        const failedIds = new Set(
          res.data.results.filter(r => !r.ok).map(r => r.payout_id)
        );
        setSelectedIds(failedIds);
      }
      
      // Refresh data
      await fetchQueue();
      await fetchSummary();
      
    } catch (err) {
      const msg = err.response?.data?.detail || 'Batch reject failed';
      toast.error(typeof msg === 'object' ? msg.message || JSON.stringify(msg) : msg);
    } finally {
      setBatchLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Tab counts
  const statusTabs = [
    { key: 'pending', label: 'Pending', count: summary?.pending_count },
    { key: 'approved', label: 'Approved', count: summary?.approved_count },
    { key: 'paid', label: 'Paid', count: summary?.paid_all_count },
    { key: 'rejected', label: 'Rejected', count: summary?.rejected_count },
    { key: 'flagged', label: 'Flagged', count: summary?.flagged_count },
    { key: 'all', label: 'All', count: null },
  ];

  return (
    <div className="min-h-screen surface-page p-3 md:p-6 overflow-x-hidden" data-testid="admin-value-payouts-page">
      <div className="max-w-7xl mx-auto">
        {/* Header - Mobile: stacked, Desktop: row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                Value Payouts
              </h1>
              <AdminHelp helpKey="value_payouts" />
            </div>
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
              Review payout requests
            </p>
          </div>
          
          {/* Action buttons - stack on mobile */}
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={exportCSV}
              className="flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4" />
              <span className="sm:inline">Export</span>
            </button>
            <button
              onClick={() => { fetchSummary(); fetchQueue(); }}
              className="flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Batch Action Bar - appears when items selected (P3-B) */}
        {selectedIds.size > 0 && (
          <div 
            className="mb-4 p-3 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 flex flex-wrap items-center gap-3"
            data-testid="batch-action-bar"
          >
            <div className="flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-rose-600" />
              <span className="text-sm font-medium text-rose-900 dark:text-rose-200">
                {selectedIds.size} selected
              </span>
              <span className="text-sm text-rose-700 dark:text-rose-300">
                (${selectedTotalAmount.toFixed(2)})
              </span>
            </div>
            
            <div className="flex-1" />
            
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-3 py-1.5 text-sm rounded-lg bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition"
              >
                Clear
              </button>
              <button
                onClick={() => setShowBatchRejectModal(true)}
                className="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition flex items-center gap-1"
                data-testid="batch-reject-btn"
              >
                <XCircle className="w-4 h-4" />
                Reject All
              </button>
              <button
                onClick={() => setShowBatchApproveModal(true)}
                className="px-3 py-1.5 text-sm rounded-lg bg-green-500 text-white hover:bg-green-600 transition flex items-center gap-1"
                data-testid="batch-approve-btn"
              >
                <CheckCircle className="w-4 h-4" />
                Approve All
              </button>
            </div>
          </div>
        )}

        {/* KPI Summary Cards - 2x2 grid always */}
        {summary && (
          <div className="grid grid-cols-2 gap-2 md:gap-3 mb-4">
            <div className="rounded-lg surface-card p-2.5 shadow-sm">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Clock className="w-3.5 h-3.5 text-yellow-500" />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Pending</span>
              </div>
              <div className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
                ${(summary.pending_amount / 100).toFixed(0)}
              </div>
              <div className="text-xs text-gray-500">{summary.pending_count} req</div>
            </div>

            <div className="rounded-lg surface-card p-2.5 shadow-sm">
              <div className="flex items-center gap-1.5 mb-0.5">
                <CheckCircle className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Approved</span>
              </div>
              <div className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
                ${(summary.approved_amount / 100).toFixed(0)}
              </div>
              <div className="text-xs text-gray-500">{summary.approved_count} ready</div>
            </div>

            <div className="rounded-lg surface-card p-2.5 shadow-sm">
              <div className="flex items-center gap-1.5 mb-0.5">
                <DollarSign className="w-3.5 h-3.5 text-green-500" />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Paid 30d</span>
              </div>
              <div className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
                ${(summary.paid_30d_amount / 100).toFixed(0)}
              </div>
              <div className="text-xs text-gray-500">{summary.paid_30d_count} done</div>
            </div>

            <div className="rounded-lg surface-card p-2.5 shadow-sm">
              <div className="flex items-center gap-1.5 mb-0.5">
                <History className="w-3.5 h-3.5 text-purple-500" />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">All-Time</span>
              </div>
              <div className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
                ${(summary.paid_all_amount / 100).toFixed(0)}
              </div>
              <div className="text-xs text-gray-500">{summary.paid_all_count} total</div>
            </div>
          </div>
        )}

        {/* Main Content: Queue + Detail */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left: Queue */}
          <div className="flex-1 lg:max-w-2xl">
            {/* Tabs - scrollable on mobile */}
            <div className="flex gap-1 mb-3 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
              {statusTabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setStatusFilter(tab.key)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 text-xs md:text-sm rounded-lg whitespace-nowrap transition ${
                    statusFilter === tab.key
                      ? 'bg-rose-500 text-white'
                      : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700'
                  }`}
                  data-testid={`tab-${tab.key}`}
                >
                  {tab.label}
                  {tab.count !== null && tab.count !== undefined && (
                    <span className={`text-xs px-1 py-0.5 rounded-full ${
                      statusFilter === tab.key
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-200 dark:bg-slate-700'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search email, ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                data-testid="search-input"
              />
            </div>

            {/* Queue List */}
            <div className="rounded-xl surface-card shadow-sm overflow-hidden">
              {loading ? (
                <div className="p-6 text-center text-gray-500">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Loading...
                </div>
              ) : queue.items.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No payout requests found
                </div>
              ) : (
                <>
                  {/* Mobile: Card view (hidden on md+) */}
                  <div className="md:hidden" data-testid="mobile-card-list">
                    {queue.items.map((item) => (
                      <PayoutMobileCard
                        key={item.id}
                        item={item}
                        isSelected={selectedPayout?.id === item.id}
                        isChecked={selectedIds.has(item.id)}
                        onClick={() => fetchDetail(item.id)}
                        onCheckboxChange={toggleSelectItem}
                      />
                    ))}
                  </div>

                  {/* Desktop: Table view (hidden below md) */}
                  <div className="hidden md:block overflow-x-auto" data-testid="desktop-table">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-slate-800/50">
                        <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                          <th className="px-3 py-2 text-left font-medium w-8">
                            <button
                              onClick={toggleSelectAll}
                              className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-slate-700"
                              data-testid="select-all-checkbox"
                            >
                              {selectedIds.size === queue.items.length && queue.items.length > 0 ? (
                                <CheckSquare className="w-5 h-5 text-rose-500" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-400" />
                              )}
                            </button>
                          </th>
                          <th className="px-3 py-2 text-left font-medium">User</th>
                          <th className="px-3 py-2 text-left font-medium">Amount</th>
                          <th className="px-3 py-2 text-left font-medium">Method</th>
                          <th className="px-3 py-2 text-left font-medium">Status</th>
                          <th className="px-3 py-2 text-left font-medium">Eligibility</th>
                          <th className="px-3 py-2 text-left font-medium">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                        {queue.items.map((item) => (
                          <PayoutTableRow
                            key={item.id}
                            item={item}
                            isSelected={selectedPayout?.id === item.id}
                            isChecked={selectedIds.has(item.id)}
                            onClick={() => fetchDetail(item.id)}
                            onCheckboxChange={toggleSelectItem}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="mt-2 text-xs text-gray-500 text-center">
              {queue.items.length} of {queue.total}
            </div>
          </div>

          {/* Right: Detail Panel */}
          <div className="flex-1 lg:max-w-xl">
            {selectedPayout ? (
              <div className="rounded-xl surface-card shadow-sm p-4 sticky top-4">
                {detailLoading ? (
                  <div className="p-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading details...
                  </div>
                ) : (
                  <>
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                        Payout Request
                      </h3>
                      <StatusBadge status={selectedPayout.status} />
                    </div>

                    {/* Amount */}
                    <div className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                      ${selectedPayout.amount_usd.toFixed(2)}
                    </div>

                    {/* Info Grid */}
                    <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">User</div>
                        <div className="text-gray-900 dark:text-gray-100 truncate">{selectedPayout.user_email}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Method</div>
                        <div className="text-gray-900 dark:text-gray-100">{selectedPayout.method}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Destination</div>
                        <div className="text-gray-900 dark:text-gray-100 truncate">{selectedPayout.destination}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Requested</div>
                        <div className="text-gray-900 dark:text-gray-100 text-xs">
                          {new Date(selectedPayout.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    {/* Eligibility at Request */}
                    <div className="mb-3">
                      <div className="text-xs font-medium text-gray-500 mb-1.5">Eligibility</div>
                      <EligibilityChips snapshot={selectedPayout.eligibility_snapshot} />
                    </div>

                    {/* Current Availability */}
                    <div className="mb-3 p-2.5 rounded-lg bg-gray-50 dark:bg-slate-800">
                      <div className="text-xs font-medium text-gray-500 mb-1.5">Current Status</div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Eligible Now:</span>
                        <span className={`font-medium ${selectedPayout.current_availability?.eligible_now ? 'text-green-600' : 'text-red-600'}`}>
                          {selectedPayout.current_availability?.eligible_now ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Available:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          ${(selectedPayout.current_availability?.eligible_cents_now / 100).toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2">
                      {selectedPayout.status === 'pending' && (
                        <>
                          <button
                            onClick={() => performAction('approve')}
                            disabled={actionLoading || !selectedPayout.current_availability?.eligible_now}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm"
                            data-testid="approve-btn"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Approve
                          </button>
                          <button
                            onClick={() => setShowRejectModal(true)}
                            disabled={actionLoading}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition text-sm"
                            data-testid="reject-btn"
                          >
                            <XCircle className="w-4 h-4" />
                            Reject
                          </button>
                          <button
                            onClick={() => performAction('flag')}
                            disabled={actionLoading}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition text-sm"
                            data-testid="flag-btn"
                          >
                            <Flag className="w-4 h-4" />
                            Flag
                          </button>
                        </>
                      )}
                      
                      {selectedPayout.status === 'approved' && (
                        <>
                          {selectedPayout.stripe_connect?.ready_for_payout ? (
                            <button
                              onClick={() => performAction('pay_via_stripe')}
                              disabled={actionLoading}
                              className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition text-sm"
                              data-testid="pay-stripe-btn"
                            >
                              <CreditCard className="w-4 h-4" />
                              Pay via Stripe
                            </button>
                          ) : (
                            <div className="p-2 rounded-lg bg-gray-100 dark:bg-slate-800 text-xs text-gray-500 dark:text-gray-400 text-center">
                              Stripe setup incomplete
                            </div>
                          )}
                          
                          <button
                            onClick={() => setShowPaidModal(true)}
                            disabled={actionLoading}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition text-sm"
                            data-testid="mark-paid-btn"
                          >
                            <DollarSign className="w-4 h-4" />
                            Mark Paid (Manual)
                          </button>
                        </>
                      )}

                      {selectedPayout.status === 'flagged' && (
                        <>
                          <button
                            onClick={() => performAction('approve')}
                            disabled={actionLoading || !selectedPayout.current_availability?.eligible_now}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Approve
                          </button>
                          <button
                            onClick={() => setShowRejectModal(true)}
                            disabled={actionLoading}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition text-sm"
                          >
                            <XCircle className="w-4 h-4" />
                            Reject
                          </button>
                        </>
                      )}
                    </div>

                    {/* Stripe Connect Status */}
                    {selectedPayout.status === 'approved' && selectedPayout.stripe_connect && (
                      <div className="mt-3 p-2.5 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                        <div className="text-xs font-medium text-purple-700 dark:text-purple-400 mb-1">
                          Stripe Connect
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-600 dark:text-gray-400">Account:</span>
                          <span className={`font-medium ${selectedPayout.stripe_connect.ready_for_payout ? 'text-green-600' : 'text-yellow-600'}`}>
                            {selectedPayout.stripe_connect.onboarding_status || 'not_started'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Paid Info */}
                    {selectedPayout.status === 'paid' && (
                      <div className="mt-3 p-2.5 rounded-lg bg-green-50 dark:bg-green-900/20">
                        <div className="text-xs font-medium text-green-700 dark:text-green-400 mb-1">
                          Payment Info
                        </div>
                        <div className="text-xs text-green-800 dark:text-green-300">
                          <div>Paid: {new Date(selectedPayout.paid_at).toLocaleString()}</div>
                          <div className="truncate">Ref: {selectedPayout.paid_reference}</div>
                        </div>
                      </div>
                    )}

                    {/* Rejection Info */}
                    {selectedPayout.status === 'rejected' && selectedPayout.decision_reason && (
                      <div className="mt-3 p-2.5 rounded-lg bg-red-50 dark:bg-red-900/20">
                        <div className="text-xs font-medium text-red-700 dark:text-red-400 mb-1">
                          Rejection Reason
                        </div>
                        <div className="text-xs text-red-800 dark:text-red-300">
                          {selectedPayout.decision_reason}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="rounded-xl surface-card shadow-sm p-6 text-center text-gray-500">
                <CreditCard className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Select a request</p>
              </div>
            )}
          </div>
        </div>

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl p-4 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Reject Request
              </h3>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason (min 5 chars)..."
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 mb-3 text-sm"
                rows={3}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowRejectModal(false); setRejectReason(''); }}
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => performAction('reject', { reason: rejectReason })}
                  disabled={rejectReason.trim().length < 5 || actionLoading}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg disabled:opacity-50 text-sm"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mark Paid Modal */}
        {showPaidModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl p-4 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Mark as Paid
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                <strong>${selectedPayout?.amount_usd.toFixed(2)}</strong> → {selectedPayout?.destination}
              </p>
              <input
                type="text"
                value={paidReference}
                onChange={(e) => setPaidReference(e.target.value)}
                placeholder="Payment reference..."
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 mb-3 text-sm"
              />
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded-lg mb-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-yellow-800 dark:text-yellow-300">
                    Creates ledger debit. Cannot undo.
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowPaidModal(false); setPaidReference(''); }}
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => performAction('mark_paid', { paid_reference: paidReference })}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50 text-sm"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Batch Approve Modal (P3-B) */}
        {showBatchApproveModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl p-5 max-w-md w-full">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Bulk Approve Payouts
                <AdminHelp helpKey="batch_payouts" size="sm" />
              </h3>
              
              {!batchResults ? (
                <>
                  {/* Summary */}
                  <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 mb-4">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                          {selectedIds.size}
                        </div>
                        <div className="text-xs text-green-600 dark:text-green-500">requests</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                          ${selectedTotalAmount.toFixed(2)}
                        </div>
                        <div className="text-xs text-green-600 dark:text-green-500">total amount</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Warning */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 rounded-lg mb-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-amber-800 dark:text-amber-300">
                        <strong>Verify eligibility.</strong> This will initiate transfers for all eligible requests. Already-paid requests will be skipped safely.
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowBatchApproveModal(false); setBatchResults(null); }}
                      className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={batchApprove}
                      disabled={batchLoading}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                      data-testid="confirm-batch-approve-btn"
                    >
                      {batchLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Approve All
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Results */}
                  <div className={`rounded-xl p-4 mb-4 ${
                    batchResults.failed === 0 
                      ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                      : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                  }`}>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div>
                        <div className="text-xl font-bold text-gray-700 dark:text-gray-300">{batchResults.total}</div>
                        <div className="text-xs text-gray-500">Total</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-green-600">{batchResults.approved}</div>
                        <div className="text-xs text-gray-500">Approved</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-blue-600">{batchResults.already_paid}</div>
                        <div className="text-xs text-gray-500">Already Paid</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-red-600">{batchResults.failed}</div>
                        <div className="text-xs text-gray-500">Failed</div>
                      </div>
                    </div>
                    {batchResults.total_paid_cents > 0 && (
                      <div className="mt-3 text-center text-sm text-green-700 dark:text-green-400">
                        Total transferred: <strong>${(batchResults.total_paid_cents / 100).toFixed(2)}</strong>
                      </div>
                    )}
                  </div>
                  
                  {/* Failed rows */}
                  {batchResults.failed > 0 && (
                    <div className="max-h-32 overflow-y-auto mb-4 text-xs">
                      <div className="font-medium text-red-700 dark:text-red-400 mb-1">Failed:</div>
                      {batchResults.results.filter(r => !r.ok).map((r, i) => (
                        <div key={i} className="text-red-600 dark:text-red-400">
                          {r.payout_id.slice(0, 8)}...: {r.error}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <button
                    onClick={() => { setShowBatchApproveModal(false); setBatchResults(null); }}
                    className="w-full px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Batch Reject Modal (P3-B) */}
        {showBatchRejectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl p-5 max-w-md w-full">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                Bulk Reject Payouts
              </h3>
              
              {!batchResults ? (
                <>
                  {/* Summary */}
                  <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 mb-4">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-red-700 dark:text-red-400">
                          {selectedIds.size}
                        </div>
                        <div className="text-xs text-red-600 dark:text-red-500">requests</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-red-700 dark:text-red-400">
                          ${selectedTotalAmount.toFixed(2)}
                        </div>
                        <div className="text-xs text-red-600 dark:text-red-500">total amount</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Rejection reason */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Rejection reason (required)
                    </label>
                    <textarea
                      value={batchRejectReason}
                      onChange={(e) => setBatchRejectReason(e.target.value)}
                      placeholder="Enter reason for rejecting these requests..."
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 text-sm"
                      rows={3}
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowBatchRejectModal(false); setBatchRejectReason(''); setBatchResults(null); }}
                      className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={batchReject}
                      disabled={batchLoading || batchRejectReason.trim().length < 5}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                      data-testid="confirm-batch-reject-btn"
                    >
                      {batchLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4" />
                          Reject All
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Results */}
                  <div className={`rounded-xl p-4 mb-4 ${
                    batchResults.failed === 0 
                      ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                      : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                  }`}>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div>
                        <div className="text-xl font-bold text-gray-700 dark:text-gray-300">{batchResults.total}</div>
                        <div className="text-xs text-gray-500">Total</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-red-600">{batchResults.rejected}</div>
                        <div className="text-xs text-gray-500">Rejected</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-blue-600">{batchResults.already_processed}</div>
                        <div className="text-xs text-gray-500">Already Done</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-amber-600">{batchResults.failed}</div>
                        <div className="text-xs text-gray-500">Failed</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Failed rows */}
                  {batchResults.failed > 0 && (
                    <div className="max-h-32 overflow-y-auto mb-4 text-xs">
                      <div className="font-medium text-red-700 dark:text-red-400 mb-1">Failed:</div>
                      {batchResults.results.filter(r => !r.ok).map((r, i) => (
                        <div key={i} className="text-red-600 dark:text-red-400">
                          {r.payout_id.slice(0, 8)}...: {r.error}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <button
                    onClick={() => { setShowBatchRejectModal(false); setBatchRejectReason(''); setBatchResults(null); }}
                    className="w-full px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
